import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  AppBar,
  Toolbar,
  Alert,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAuth } from '../contexts/AuthContext';
import LogoutButton from '../components/LogoutButton';
import { getActiveRegions } from '../services/regions.services';

//Region Interface
interface Region {
  id: string;
  name: string;
  location: string;
  state: string;
}

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits')
    .required('Phone number is required'),
  address: Yup.string().required('Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zipCode: Yup.string()
    .matches(/^[0-9]{5}$/, 'Zip code must be 5 digits')
    .required('Zip code is required'),
  regionId: Yup.string().required('Region is required'),
});

//Styled components for better readability
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6),
  },
}));

//StyledForm is used to style the Formik form
const StyledForm = styled(Form)({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
});

const VolunteerRegistration: React.FC = () => {
  //Use useAuth to get authStatus and completeRegistration function
  const { authStatus, completeRegistration } = useAuth();
  //State to manage regions
  const [regions, setRegions] = useState<Region[]>([]);
  //Use useTheme and useMediaQuery to handle responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  //Steps for the stepper panel
  const steps = ['Personal Info', 'Region Info'];
  const [activeStep, setActiveStep] = useState(0);
  //State to handle registration errors
  const [registrationError, setRegistrationError] = useState<string | null>(
    null
  );

  //Values for the registration form
  const initialValues = {
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    regionId: '',
  };

  useEffect(() => {
    fetchRegions();
  }, []);

  //Fetch regions that are active for the user to select
  const fetchRegions = async () => {
    try {
      //Get all active regions
      const rawData = await getActiveRegions();
      //Map the raw data from the db to the Region interface
      const data: Region[] = rawData.map((region: any) => ({
        id: region.id,
        name: region.name,
        location: region.location,
        state: region.state,
      }));
      //Set the regions hook with the fetched data
      setRegions(data);
    } catch (err) {
      console.error('Error fetching regions:', err);
      setRegistrationError('Failed to load regions. Please try again later.');
    }
  };

  //When the user completes their registration and signs up
  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      //Reset the registration error state
      setRegistrationError(null);

      //Check if the user is authenticated
      if (!authStatus.user) {
        throw new Error('User not authenticated');
      }

      //Create the registration data object and set the userType to 'volunteer'
      const registrationData = {
        ...values,
        userType: 'volunteer',
      };

      //Submit registration data to complete the registration process (After Registration is completed, AccessGuard will handle navigation to volunteer dashboard)
      await completeRegistration(registrationData);
    } catch (error) {
      console.error('Registration error:', error);
      setRegistrationError(
        'An error occurred during registration. Please try again.'
      );
      //Set the submitting state to false to allow the user to try again
      setSubmitting(false);
    }
  };

  return (
    <>
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            Volunteer Registration
          </Typography>
          <LogoutButton />
        </Toolbar>
      </AppBar>
      <Box sx={{ maxWidth: 600, margin: 'auto', mt: 4, px: 2 }}>
        <StyledPaper elevation={3}>
          <Typography variant="h4" gutterBottom align="center" color="primary">
            Complete Your Registration
          </Typography>

          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isMobile}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ mb: 4 }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {registrationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {registrationError}
            </Alert>
          )}

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting, setTouched, validateForm }) => (
              <StyledForm>
                {activeStep === 0 && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="firstName"
                          label="First Name"
                          error={touched.firstName && Boolean(errors.firstName)}
                          helperText={touched.firstName && errors.firstName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="lastName"
                          label="Last Name"
                          error={touched.lastName && Boolean(errors.lastName)}
                          helperText={touched.lastName && errors.lastName}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="phone"
                          label="Phone Number"
                          error={touched.phone && Boolean(errors.phone)}
                          helperText={touched.phone && errors.phone}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="address"
                          label="Address"
                          error={touched.address && Boolean(errors.address)}
                          helperText={touched.address && errors.address}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="city"
                          label="City"
                          error={touched.city && Boolean(errors.city)}
                          helperText={touched.city && errors.city}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="state"
                          label="State"
                          error={touched.state && Boolean(errors.state)}
                          helperText={touched.state && errors.state}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="zipCode"
                          label="Zip Code"
                          error={touched.zipCode && Boolean(errors.zipCode)}
                          helperText={touched.zipCode && errors.zipCode}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
                {activeStep === 1 && (
                  <>
                    <FormControl
                      fullWidth
                      error={touched.regionId && Boolean(errors.regionId)}
                    >
                      <InputLabel id="region-select-label">Region</InputLabel>
                      <Field
                        as={Select}
                        labelId="region-select-label"
                        id="region-select"
                        name="regionId"
                        label="Region"
                      >
                        {regions.length > 0 ? (
                          regions.map((region) => (
                            <MenuItem key={region.id} value={region.id}>
                              {region.name} ({region.location}, {region.state})
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled value="">
                            Loading regions...
                          </MenuItem>
                        )}
                      </Field>
                      {touched.regionId && errors.regionId && (
                        <Typography
                          color="error"
                          variant="caption"
                          sx={{ ml: 2, mt: 0.5 }}
                        >
                          {errors.regionId}
                        </Typography>
                      )}
                    </FormControl>
                  </>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep((prev) => prev - 1)}
                  >
                    Back
                  </Button>
                  {activeStep === steps.length - 1 ? (
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Complete Registration'}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      onClick={async () => {
                        const allErrors = (await validateForm()) as Record<
                          string,
                          string
                        >;

                        const fieldsToCheck =
                          activeStep === 0
                            ? [
                                'firstName',
                                'lastName',
                                'phone',
                                'address',
                                'city',
                                'state',
                                'zipCode',
                              ]
                            : ['regionId'];

                        const touchedFields = fieldsToCheck.reduce(
                          (acc, field) => {
                            acc[field] = true;
                            return acc;
                          },
                          {} as Record<string, boolean>
                        );

                        await setTouched(touchedFields, true);

                        const hasErrors = fieldsToCheck.some((field) =>
                          Boolean(allErrors[field])
                        );

                        if (!hasErrors) {
                          setActiveStep((prev) => prev + 1);
                        }
                      }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </StyledForm>
            )}
          </Formik>
        </StyledPaper>
      </Box>
    </>
  );
};

export default VolunteerRegistration;
